import type { Dispatch, SetStateAction } from 'react';
import type { ApplePayElementProps } from '@adyen/adyen-web/dist/types/components/ApplePay/types';
import type { FetchResult } from '@apollo/client';
import type { Cart } from '../../../codegen/types';
import type { PlaceOrderMutation } from '../../../graphqlDocument/order/placeOrder.generated';
import { sendAddPaymentInfoEvent } from '../../../utils/gtm/events/add_payment_info/sendAddPaymentInfoEvent';
import { setPaymentMethodInStorage } from '../utils/setPaymentMethodInStorage';

// Adyen docs - web components - Apple Pay
// https://docs.adyen.com/payment-methods/apple-pay/web-component/

export interface CreateApplePayConfigProps {
  miniCart: Cart | Record<string, never>;
  cartId: string;
  baseUrl: string;
  setIsPaymentBtnDisabled: Dispatch<SetStateAction<boolean>>;
  setPaymentInProgress: Dispatch<SetStateAction<boolean>>;
  setPaymentError: Dispatch<SetStateAction<boolean>>;
  validateDiscountsBeforePayment: () => Promise<boolean>;
  placeOrderMutation: () => Promise<FetchResult<PlaceOrderMutation, Record<string, unknown>, Record<string, unknown>>>;
  onBeforeSubmit: (data: unknown, actions: unknown) => Promise<void>;
}

export const createApplePayConfig = ({
  miniCart,
  cartId,
  baseUrl,
  setIsPaymentBtnDisabled,
  setPaymentInProgress,
  setPaymentError,
  validateDiscountsBeforePayment,
  placeOrderMutation,
  onBeforeSubmit,
}: CreateApplePayConfigProps): Partial<ApplePayElementProps> => ({
  // TODO: The countryCode is required by default for Apple Pay - confirm if it's necessary
  showPayButton: true,
  buttonType: 'check-out',
  buttonColor: 'black',
  beforeSubmit: async (data, _, actions) => {
    setIsPaymentBtnDisabled(true);
    setPaymentInProgress(true);

    const isDiscountValid = await validateDiscountsBeforePayment();

    if (!isDiscountValid) {
      actions.reject();

      setPaymentInProgress(false);
      setIsPaymentBtnDisabled(false);

      return;
    }

    const result = await placeOrderMutation();

    if (result.data?.placeOrder.accepted) {
      sendAddPaymentInfoEvent(miniCart, 'apple pay', cartId, baseUrl);
      setPaymentMethodInStorage('applepay', 'ApplePay');
      onBeforeSubmit(data, actions);
    } else {
      actions.reject();

      setPaymentError(true);
      setIsPaymentBtnDisabled(false);
      setPaymentInProgress(false);
    }
  },
});
